import { useState } from 'react';
import type { InteractiveMode } from '@/types';
import { cn } from '@/lib/utils';
import { DocumentChatCard } from './DocumentChatCard';
import { PlanCard } from './PlanCard';

/** Golden-highlighted free-text input step (e.g. the Zoom app-name prompt),
 *  with an optional one-click "use suggested name" button. The amber ring +
 *  guide-glow match the highlighted-choice treatment (the Read-only step) so
 *  the user's eye is drawn to the input awaiting them. */
function InputStep({ interactive, onSelect, disabled }: InteractiveOptionsProps) {
  const [value, setValue] = useState('');
  const submit = (v: string) => {
    const trimmed = v.trim();
    if (trimmed) onSelect(trimmed);
  };
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); submit(value); }}
      className="mt-4 rounded-xl border-2 border-amber-400/60 bg-amber-500/5 p-4 guide-glow"
    >
      {interactive.prompt && (
        <p className="mb-2 text-sm font-medium text-foreground">{interactive.prompt}</p>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        placeholder={interactive.suggestion ? `e.g. ${interactive.suggestion}` : 'Type a name…'}
        autoFocus
        className="w-full rounded-lg border border-amber-400/40 bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-400/60"
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {interactive.suggestion && (
          <button
            type="button"
            onClick={() => submit(interactive.suggestion!)}
            disabled={disabled}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50"
          >
            Use suggested name: {interactive.suggestion}
          </button>
        )}
        <button
          type="submit"
          disabled={disabled || !value.trim()}
          className="inline-flex items-center rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary/70 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Use this name
        </button>
      </div>
    </form>
  );
}

interface InteractiveOptionsProps {
  interactive: InteractiveMode;
  onSelect: (value: string) => void;
  disabled?: boolean;
  /** A request's card becomes its plan's once the plan is made (DocumentChatCard). */
  onReplace?: (next: InteractiveMode) => void;
}

const noop = (): void => {};

const iconMap: Record<string, string> = {
  plan: '📋',
  quick: '⚡',
  manual: '📖',
  product: '📦',
  confirm: '✓',
  cancel: '✗',
  watch: '👀',
};

export function InteractiveOptions({ interactive, onSelect, disabled, onReplace }: InteractiveOptionsProps) {
  // Free-text input step (e.g. the Zoom app-name prompt) — golden input + a
  // one-click suggested-name button.
  if (interactive.type === 'input') {
    return <InputStep interactive={interactive} onSelect={onSelect} disabled={disabled} />;
  }
  if (interactive.type !== 'choice' || !interactive.options?.length) {
    return null;
  }

  return (
    <div className="mt-4">
      {interactive.document && <DocumentChatCard id={interactive.document.id} onPlanned={onReplace ?? noop} />}
      {interactive.plan && <PlanCard plan={interactive.plan} />}
      <div className="flex flex-wrap gap-2">
        {interactive.options.map((option) => (
          <button
            key={option.id}
            onClick={() => {
              if (option.url) {
                window.open(option.url, 'zac-zoom-oauth', 'popup,width=600,height=700');
              } else {
                onSelect(option.value);
              }
            }}
            disabled={disabled || option.disabled}
            title={option.hint}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg',
              'bg-blue-600 hover:bg-blue-500 border border-blue-600',
              'text-sm font-medium text-white',
              'transition-colors duration-150',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600',
              'focus:outline-none focus:ring-2 focus:ring-blue-500/50',
              // Golden guidance halo — steers the user to the recommended option.
              option.highlight && !option.disabled &&
                'guide-glow ring-2 ring-amber-400 ring-offset-2 ring-offset-background'
            )}
          >
            {option.icon && (
              <span className="text-base">{iconMap[option.icon] || '•'}</span>
            )}
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
